import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  Divider,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from "@mui/material/Badge";
import CloseIcon from "@mui/icons-material/Close";

// import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import PeopleIcon from "@mui/icons-material/People";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import BuildIcon from "@mui/icons-material/Build";
import "./navbar.css";
import { NotificationContext } from "../utils/NotificationContext";
import Menu from '@mui/material/Menu';

const Navbar = () => {
  const [userType, setUserType] = useState(localStorage.getItem("userType"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userId, setUserId] = useState(localStorage.getItem("urlID"));
  const [token, setToken] = useState(localStorage.getItem("token"))
  // const { notifications, markAllAsViewed } = useContext(NotificationContext);
  const { notifications, markNotificationAsViewed, removeNotification } = useContext(NotificationContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleNotificationClick = (event) => {
    const relevantNotifications = notifications.filter(
      (n) => n.recipientType?.includes(userType)
    );

    if (relevantNotifications.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setUserType(localStorage.getItem("userType"));
    setUserId(localStorage.getItem("urlID"));
    setToken(localStorage.getItem("token"));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userType");
    localStorage.removeItem("user id");
    localStorage.removeItem("token");
    setUserType(null);
    navigate("/login");
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  /** Smooth Scrolling Function */
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  /** Scroll to Top when clicking "Home" on the same page */
  const handleHomeClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  /** Role-Based Navigation Links */
  const getNavLinks = () => {
    if (location.pathname === "/login" || location.pathname === "/") {
      return [
        { label: "Home", icon: <HomeIcon />, onClick: handleHomeClick },
        { label: "Services", icon: <BuildIcon />, onClick: () => scrollToSection("services") },
        // { label: "About Us", icon: <InfoIcon />, onClick: () => scrollToSection("about") },
        { label: "Contact Us", icon: <ContactMailIcon />, onClick: () => scrollToSection("contact") },
        { to: "/login", label: "Client Login" },
      ];
    } else if (userType === "admin" || userType === "team_leader" || userType === "BSE") {
      return [
        { to: "/vessel_owner_table", label: "Vessel Owner", icon: <PersonOutlineIcon /> },
        { to: "/VesselManagerTable", label: "Vessel Manager", icon: <PeopleIcon /> },
        { to: "/vessel_Table", label: "Vessels", icon: <DirectionsBoatIcon /> },
        { to: "/CrewingAgentTable", label: "Crewing Agent", icon: <PeopleIcon /> },
        { to: "/CrewProfileTable", label: "Crew", icon: <GroupIcon /> },
        { to: "/ContractTable", label: "Contract", icon: <AssignmentIcon /> },
        {
          component: (
            <IconButton color="inherit" onClick={handleNotificationClick}>
              <Badge
                badgeContent={notifications.filter(n => n.recipientType?.includes(userType) && !n.viewed).length}
                color="error"
                 max={999999}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          ),
        },
      ];
    } else if (userType === "Vessel_vendor") {
      return [{ to: "/Vesselvendor", label: "Vessel Vendor" }];
    } else if (userType === "crewing_agent") {
      return [
        { to: `/ContractTable/${userId}`, label: "Contract Table", icon: <AssignmentIcon /> },
        { to: `/vesselsforagent/${userId}`, label: "Vessels", icon: <DirectionsBoatIcon /> },
        { to: `/CrewAssigned/${userId}`, label: "Crew", icon: <GroupIcon /> },
        {
          component: (
            <IconButton color="inherit" onClick={handleNotificationClick}>
              <Badge
                badgeContent={notifications.filter(n => n.recipientType === "crewing_agent" && !n.viewed).length}
                color="error"
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          ),
        },
      ];
    }
    return [];
  };

  const navLinks = getNavLinks();
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationItemClick = async (notificationId) => {
    await markNotificationAsViewed(notificationId);
  }

  return (
    <AppBar position="sticky" className="navbar">
      <Toolbar className="toolbar">
        {/* Logo */}
        {userType === "admin" || userType === "BSE" ? (
          <Typography variant="h6" component={Link} to="/AdminDashboard" className="logo">
            <img src="/assets/bsplLogoREmover.png" alt="logo" className="logoImage" />
          </Typography>
        ) : (
          <Typography variant="h6" component={Link} to="/" className="logo">
            <img src="/assets/bsplLogoREmover.png" alt="logo" className="logoImage" />
          </Typography>
        )}



        {/* Desktop Navigation */}
        {!isMobile && (
          <Box className="desktop-nav">
            {navLinks.map((link, index) =>
              link.component ? (
                // ✅ Renders custom component (like notification icon)
                <Box key={index} className="nav-link">
                  {link.component}
                </Box>
              ) : (
                // ✅ Default rendering for regular links
                <Button
                  key={index}
                  component={link.to ? Link : "button"}
                  to={link.to || undefined}
                  onClick={link.onClick || undefined}
                  className="nav-link"
                >
                  {link.icon} {link.label}
                </Button>
              )
            )}

            {/* Logout button */}
            {!navLinks.some(link => link.label === "Client Login") && !!userType && (
              <Button onClick={handleLogout} className="logout-btn">
                <LogoutIcon /> Logout
              </Button>
            )}
          </Box>
        )}

      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 580,
            p: 1,
            borderRadius: 3,
            boxShadow: 3,
            overflowY: 'auto',
            backgroundColor: "background.paper"
          }
        }}
      >
        {notifications.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            No notifications
          </Typography>
        ) : (
          notifications.filter(n => n.recipientType?.includes(userType))
            .map((notif) => (
              <Box
                key={notif._id}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 1.5,
                  my: 1,
                  backgroundColor: notif.viewed ? "#f9f9f9" : "#e3f2fd",
                  borderLeft: notif.viewed ? "4px solid transparent" : "4px solid #1976d2",
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: notif.viewed ? "#f0f0f0" : "#d2e5fa",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                {/* Dismiss (X) icon */}
                <IconButton
                  size="small"
                  sx={{ position: "absolute", top: 8, right: 8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNotification(notif._id);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                {/* Message and timestamp */}
                <Box onClick={() => handleNotificationItemClick(notif._id)}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={notif.viewed ? 400 : 600}
                    color={notif.viewed ? "text.secondary" : "text.primary"}
                    sx={{ pr: 4 }}
                  >
                    {notif.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {new Date(notif.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))
        )}
      </Menu>


      {/* Mobile Drawer */}
      <Drawer anchor="left" open={mobileOpen} onClose={toggleMobileMenu} className="mobile-drawer">
        <List className="mobile-menu">
          {navLinks.map((link, index) => (
            <ListItem
              key={index}
              button
              component={link.to ? Link : "button"}
              to={link.to || undefined}
              onClick={() => {
                if (link.onClick) link.onClick();
                toggleMobileMenu();
              }}
            >
              {link.icon} {link.label}
            </ListItem>
          ))}
          <Divider />
          {!!userType && (
            <ListItem button onClick={handleLogout} className="mobile-logout">
              <LogoutIcon /> Logout
            </ListItem>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
